/**
 * Util to help pre-fetching of initial model data
 */
import clientServerBridge from '@helpcenter/client-server-bridge';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import { getRequest } from 'relay-runtime';
import type { ConnectFragmentsResponse, ProfileConnectFragmentsResponse } from 'rest/connect-addon';
import type { FeaturedAndSortedPortalsResponse } from 'rest/featured-and-sorted-portals';
import type { HCBrandingResponse } from 'rest/help-center-branding';
import type { LoginAnnouncementsResponse } from 'rest/login-announcements';
import type {
    AiAnswerFeedbackResponse,
    AnonymousApprovalResponse,
    ArticleResponse,
    CompleteSignupResponse,
    LanguageResponse,
    ResetPasswordResponse,
    SignUpResponse,
    TimezoneRegionResponse,
    TimezoneResponse,
    UserResponse,
    VerifyEmailResponse,
    VisitPortalResponse,
} from 'rest/models';
import type { OrganisationsResponse } from 'rest/organisations';
import type { PortalBrandingResponse, PortalResponse } from 'rest/portal';
import type { RequestCreateResponse } from 'rest/request-create';
import type { RequestDetailsResponse } from 'rest/request-details';
import type { RequestListResponse } from 'rest/request-list';
import type { RequestsColumnSettingsResponse } from 'rest/requests-column-settings';
import type { ForgotPasswordResponse } from 'state/actions/forgot-password';
import type { HelpCenterResponse } from 'state/actions/help-center';
import type { LoginResponse } from 'state/actions/login';
import type { UnsubscribedConfirmationResponse } from 'state/actions/unsubscribed-confirmation';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { isKoshEnabledForDefaultHelpCenter } from '../advanced-help-center';
import { convertToHCBrandingResponse } from '../graphql-relay-connector';
import BrandingQuery from '../graphql-relay-connector/help-center-branding/__generated__/helpCenterBrandingQuery.graphql';
import { QueryResponseCache } from '../relay/query-responses';

export interface InitialModelData {
    user?: UserResponse;
    helpCenterBranding?: HCBrandingResponse;
    sharedPortal?: HelpCenterResponse;
    unsubscribedConfirmation?: UnsubscribedConfirmationResponse;
    login?: LoginResponse;
    forgotPassword?: ForgotPasswordResponse;
    reqCreate?: RequestCreateResponse;
    portal?: PortalResponse;
    reqDetails?: RequestDetailsResponse;
    kbPage?: ArticleResponse;
    timezoneRegions?: TimezoneRegionResponse[];
    timezones?: TimezoneResponse[];
    availableLanguages?: LanguageResponse[];
    profileWebFragments?: ProfileConnectFragmentsResponse;
    portalWebFragments?: ConnectFragmentsResponse;
    allReqFilter?: RequestListResponse;
    requestsColumnSettings?: RequestsColumnSettingsResponse;
    organisations?: OrganisationsResponse;
    visitPortal?: VisitPortalResponse;
    resetPassword?: ResetPasswordResponse;
    completeSignup?: CompleteSignupResponse;
    verifyEmail?: VerifyEmailResponse;
    signUp?: SignUpResponse;
    branding?: PortalBrandingResponse;
    anonymousApproval?: AnonymousApprovalResponse;
    featuredAndSortedPortals?: FeaturedAndSortedPortalsResponse;
    loginAnnouncements?: LoginAnnouncementsResponse;
    aiAnswerFeedbackPage?: AiAnswerFeedbackResponse;
}

let cachedObject: InitialModelData;
let ssrBrandingResponse: HCBrandingResponse | null | undefined = undefined;

export default () => {
    const jsonPayload = clientServerBridge.getElementById('jsonPayload');
    if (!cachedObject) {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        cachedObject = (jsonPayload && jsonPayload.textContent && JSON.parse(jsonPayload.textContent)) || {};
    }
    if (isKoshEnabledForDefaultHelpCenter()) {
        delete cachedObject.helpCenterBranding;
        delete cachedObject.featuredAndSortedPortals;
        delete cachedObject.loginAnnouncements;
        /**
         * If we're on the client and we have a cached response from SSR query cache, use it right away.
         */
        if (FeatureGates.checkGate('hydrate_jsm_ssr_branding') && !__SERVER__) {
            if (ssrBrandingResponse === undefined) {
                const ssrCache = QueryResponseCache;

                const reqId = getRequest(BrandingQuery).params.id;
                const resp =
                    reqId &&
                    ssrCache.get(reqId, {
                        cloudId: getCloudId(),
                        helpCenterAri: getHelpCenterAri(),
                    });

                if (resp !== null) {
                    // @ts-ignore
                    ssrBrandingResponse = convertToHCBrandingResponse(resp.data);
                } else {
                    ssrBrandingResponse = null;
                }
            }
            if (ssrBrandingResponse !== null && ssrBrandingResponse !== undefined) {
                cachedObject.helpCenterBranding = ssrBrandingResponse;
            }
        }
    }
    return cachedObject;
};

// Export this internally so we can delete it when testing.
export const deleteCachedObject = () => {
    cachedObject = undefined as unknown as InitialModelData;
    ssrBrandingResponse = undefined;
};
